import { useTheme } from '@emotion/react'
import { isCorporateAccount } from '@news-mono/component-library'
import {
    AppState,
    BreachEvent,
    isCookieValidSubscriptionPromptCookie,
    THEWEST_ALL,
    THEWEST_PREMIUM,
    THEWEST_REPLICA,
    useFeature,
    WESTREWARDS,
} from '@news-mono/web-common'
import Cookies from 'js-cookie'
import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { useSelector } from 'react-redux'
import {
    OnSubscribeEventHandler,
    useAuthActions,
} from '../../user-registration/authentication/AuthenticationProvider'
import { SwgPurchaseCompletePrompt } from '../../user-registration/breach/components/PurchaseCompletePrompt/PurchaseCompletePrompt'
import { SubscriptionPrompt } from '../../user-registration/breach/components/SubscriptionPrompt/SubscriptionPrompt'
import { createSubscribeLinkClickEvent } from '../../user-registration/subscribe/createSubscribeClickLinkHandler'
import { TheWestHeaderDropdown } from '../TheWestHeaderDropdown'
import { LogoutActionLink } from './LogoutActionLink'
import { RegionalActionLink } from './RegionalActionLink'
import {
    NewStyledActionButton,
    NewStyledActionLinks,
    NewStyledPremiumIcon,
    StyledActionButton,
    StyledActionLinks,
    StyledCartContainer,
    StyledEmptyCartIcon,
    StyledFullCartIcon,
    StyledSkeleton,
    subscribeButtonClassname,
} from './TheWestHeaderLogin.styled'
import { UserName } from './UserNameLink'

export interface AuthSiteLoginActionLinksProps {
    onEvent: (event: any) => void
}

export interface EntitlementDetail {
    isAllEntitled: boolean
    isReplicaEntitled: boolean
    isPremiumEntitled: boolean
    isSiteLicence: boolean
    isWestRewardsEntitled: boolean
}

export const AuthSiteLoginActionLinks: React.FC<AuthSiteLoginActionLinksProps> =
    ({ onEvent }) => {
        const {
            userName,
            isLoggedIn,
            hasAttemptedValidation,
            entitlements,
            subscriptionType,
        } = useSelector((state: AppState) => state.authentication)
        const { section } = useTheme()
        const isRegionalSite = section !== 'default'
        const { onLoginClick, onSubscribeClick, onLogoutClick } =
            useAuthActions()
        const [
            isPurchaseCompletePromptMounted,
            setPurchaseCompletePromptMounted,
        ] = useState(false)
        const isRrmeEnabled = useFeature('google-rrme-full-override')

        /**
         * Check to see if the user likely has an account on SSW before we link there,
         * Regwall users only have `thewest:registered`
         */
        const showMyAccountLink = isLoggedIn && !!entitlements

        const entitlementsDetail: EntitlementDetail = {
            isAllEntitled:
                entitlements.includes(THEWEST_ALL) &&
                !isCorporateAccount(subscriptionType),
            isReplicaEntitled:
                entitlements.includes(THEWEST_ALL) ||
                entitlements.includes(THEWEST_REPLICA),
            isPremiumEntitled:
                entitlements.includes(THEWEST_ALL) ||
                entitlements.includes(THEWEST_PREMIUM),
            isWestRewardsEntitled: entitlements.includes(WESTREWARDS),
            isSiteLicence: isCorporateAccount(subscriptionType),
        }

        useEffect(() => {
            setPurchaseCompletePromptMounted(true)
        }, [])

        return (
            <React.Fragment>
                {/* Wait for document to become available, then mount the prompt to the body */}
                {isRrmeEnabled &&
                    isPurchaseCompletePromptMounted &&
                    ReactDOM.createPortal(
                        <SwgPurchaseCompletePrompt onEvent={onEvent} />,
                        document.body,
                    )}
                {isLoggedIn ? (
                    <ActionLinksAuthenticated
                        isRegionalSite={isRegionalSite}
                        userName={userName}
                        hasAccount={showMyAccountLink}
                        entitlements={entitlementsDetail}
                        onLogoutClick={() => onLogoutClick(onEvent)}
                        onEvent={onEvent}
                    />
                ) : (
                    <ActionLinksUnauthenticated
                        isRegionalSite={isRegionalSite}
                        hasAttemptedValidation={hasAttemptedValidation}
                        onEvent={onEvent}
                        onLoginClick={() => onLoginClick('header')}
                        onSubscribeClick={onSubscribeClick}
                    />
                )}
            </React.Fragment>
        )
    }

export interface ActionLinksUnauthenticatedProps {
    isRegionalSite: boolean
    onLoginClick: () => void
    onSubscribeClick: OnSubscribeEventHandler
    hasAttemptedValidation: boolean
    onEvent: (event: any) => void
}

export const ActionLinksUnauthenticated: React.FC<ActionLinksUnauthenticatedProps> =
    ({
        isRegionalSite,
        hasAttemptedValidation,
        onLoginClick,
        onSubscribeClick,
        onEvent,
    }) => (
        <NewStyledActionLinks>
            {isRegionalSite && <RegionalActionLink />}
            {hasAttemptedValidation ? (
                <React.Fragment>
                    <StyledActionButton
                        isPrimary={false}
                        onClick={onLoginClick}
                    >
                        Login
                    </StyledActionButton>
                    <NewStyledActionButton
                        className={subscribeButtonClassname}
                        onClick={() => {
                            createSubscribeLinkClickEvent(
                                'header-utility',
                                onEvent,
                            )()
                            onSubscribeClick({
                                callToAction: 'header-utility',
                            })
                        }}
                        isPrimary={true}
                    >
                        Subscribe
                    </NewStyledActionButton>
                    <CartIconLink isLoggedIn={false} onEvent={onEvent} />
                </React.Fragment>
            ) : (
                <React.Fragment>
                    <StyledSkeleton count={1} />
                    <StyledSkeleton count={1} />
                </React.Fragment>
            )}
        </NewStyledActionLinks>
    )

export interface ActionLinksAuthenticatedProps {
    isRegionalSite: boolean
    userName: string
    hasAccount: boolean
    entitlements: EntitlementDetail
    onLogoutClick: () => void
    onEvent: (event: any) => void
}

export const ActionLinksAuthenticated: React.FC<ActionLinksAuthenticatedProps> =
    ({
        isRegionalSite,
        userName,
        hasAccount,
        onLogoutClick,
        entitlements,
        onEvent,
    }) => (
        <StyledActionLinks>
            {isRegionalSite && <RegionalActionLink />}
            {hasAccount ? (
                <>
                    <TheWestHeaderDropdown
                        userName={userName}
                        onLogoutClick={onLogoutClick}
                        entitlements={entitlements}
                        // isEntitled={isEntitled}
                        onEvent={onEvent}
                    />
                    <CartIconLink isLoggedIn={true} onEvent={onEvent} />
                </>
            ) : (
                <>
                    <UserName userName={userName} />
                    <CartIconLink isLoggedIn={true} onEvent={onEvent} />
                    <LogoutActionLink onLogoutClick={onLogoutClick} />
                </>
            )}
        </StyledActionLinks>
    )

export const CartIconLink = ({
    isLoggedIn,
    onEvent,
}: {
    isLoggedIn: boolean
    onEvent: (event: BreachEvent) => void
}) => {
    const { getSubscribeLink } = useAuthActions()
    const [cookieString, setCookieString] = useState(
        Cookies.get('subscribeCart'),
    )
    const cartCookie = isCookieValidSubscriptionPromptCookie(cookieString)

    const [isSubscriptionCartPromptMounted, setSubscriptionCartPromptMounted] =
        useState(false)

    useEffect(() => {
        setSubscriptionCartPromptMounted(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // If cart cookie isn't valid, clear it.
    useEffect(() => {
        if (cookieString && cartCookie === false) {
            Cookies.remove('subscribeCart')
        }
    }, [cookieString, cartCookie])

    if (cartCookie && cartCookie.package_info) {
        const subscribeUrl = getSubscribeLink({
            packagePath: cartCookie.href || undefined,
        })

        return (
            <StyledCartContainer>
                {isSubscriptionCartPromptMounted &&
                    ReactDOM.createPortal(
                        <SubscriptionPrompt
                            refreshCart={() =>
                                setCookieString(Cookies.get('subscribeCart'))
                            }
                            promptData={cartCookie}
                            onEvent={onEvent}
                        />,
                        document.body,
                    )}
                <a href={subscribeUrl} target="_blank" title="Shopping cart">
                    <StyledFullCartIcon />
                </a>
            </StyledCartContainer>
        )
    }

    return isLoggedIn ? null : (
        <StyledCartContainer>
            <StyledEmptyCartIcon />
        </StyledCartContainer>
    )
}
